<template>
  <div>
    <transition name="top-search-fade">
      <div v-show="open" class="top-search-backdrop" @click="$emit('close')"></div>
    </transition>
    <transition name="top-search-sheet">
      <div
        v-show="open"
        class="top-search-panel"
        :class="{ dragging: dragOffset !== null, 'top-search-panel-full': fullHeight }"
        :style="[panelStyle, dragStyle]"
        role="dialog"
        :aria-label="ariaLabel"
      >
        <div
          class="top-search-panel-handle"
          role="button"
          aria-label="閉じる"
          @click.prevent="$emit('close')"
          @touchstart.passive="handleDragStart"
          @touchmove="handleDragMove"
          @touchend="handleDragEnd"
          @touchcancel="handleDragEnd"
        >
          <span></span>
        </div>
        <slot name="head"></slot>
        <div class="top-search-panel-body">
          <slot></slot>
        </div>
        <div class="top-search-panel-foot">
          <slot name="foot"></slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const MOBILE_MAX_WIDTH = 767
const SWIPE_CLOSE_DISTANCE = 80 // px dragged down on the handle before the sheet closes

// Shell shared by the search form's popovers: a dropdown anchored under a field on desktop,
// a bottom sheet with a drag handle and backdrop on mobile. Always rendered (toggled with v-show)
// because vue2-teleport only moves nodes that exist at mount time.
export default {
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    // Viewport-relative box of the field the dropdown hangs from (desktop only).
    anchorRect: {
      type: Object,
      default: null,
    },
    width: {
      type: Number,
      default: 560,
    },
    ariaLabel: {
      type: String,
      required: true,
    },
    // Mobile: fill the viewport (long lists) instead of sizing the sheet to its content.
    fullHeight: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dragStartY: null,
      dragOffset: null,
    }
  },
  computed: {
    panelStyle() {
      // Mobile: bottom sheet sized by the stylesheet.
      if (!this.anchorRect || window.innerWidth <= MOBILE_MAX_WIDTH) return null

      const margin = 16
      const width = Math.min(this.width, window.innerWidth - margin * 2)
      const top = this.anchorRect.bottom + 8
      const left = Math.max(margin, Math.min(this.anchorRect.left, window.innerWidth - width - margin))

      return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        maxHeight: `${Math.max(240, window.innerHeight - top - margin)}px`,
      }
    },
    dragStyle() {
      if (!this.dragOffset) return null

      return { transform: `translateY(${this.dragOffset}px)` }
    },
  },
  methods: {
    handleDragStart(event) {
      this.dragStartY = event.touches[0].clientY
      this.dragOffset = 0
    },
    handleDragMove(event) {
      if (this.dragStartY === null) return

      // Only follow downward drags; the page behind must not scroll while the handle is held.
      event.preventDefault()
      this.dragOffset = Math.max(0, event.touches[0].clientY - this.dragStartY)
    },
    handleDragEnd() {
      const shouldClose = this.dragOffset !== null && this.dragOffset > SWIPE_CLOSE_DISTANCE
      this.dragStartY = null
      this.dragOffset = null
      if (shouldClose) {
        this.$emit('close')
      }
    },
  },
}
</script>
